import React, { useMemo, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { FORECAST_USERS } from '../forecastUsers';
import { ForecastChangeDrawer, ForecastValidationList } from '../ForecastWorkspace';

const ACTIONS_BY_STATUS = {
  draft: ['submit', 'cancel'],
  in_review: ['approve', 'return', 'recall', 'owner_handoff'],
  approved: ['load', 'reopen', 'cancel', 'owner_handoff'],
  loaded: ['reopen', 'archive', 'owner_handoff'],
  cancelled: ['archive'],
  archived: []
};

const ACTION_COPY = {
  submit: {
    label: 'Submit for FP&A review',
    title: 'Submit project',
    prompt: 'Explain why this project is ready for FP&A review.'
  },
  approve: {
    label: 'Approve project pro forma',
    title: 'Approve project',
    prompt: 'Document the approval basis and any open monitoring points.'
  },
  return: {
    label: 'Return to draft',
    title: 'Return project',
    prompt: 'Explain what the project owner needs to revise.'
  },
  recall: {
    label: 'Recall to draft',
    title: 'Recall project',
    prompt: 'Explain why the submitted project is being recalled.'
  },
  reopen: {
    label: 'Reopen for changes',
    title: 'Reopen project',
    prompt: 'Explain why the approved or loaded project must be reopened.'
  },
  cancel: {
    label: 'Cancel project',
    title: 'Cancel project',
    prompt: 'Document why the project is being cancelled.'
  },
  archive: {
    label: 'Archive project',
    title: 'Archive project',
    prompt: 'Document why this terminal project can be archived.'
  },
  owner_handoff: {
    label: 'Hand off owner',
    title: 'Hand off accountable owner',
    prompt: 'Document the handoff reason and follow-up expectations.'
  }
};

const ownerOptions = FORECAST_USERS.filter((user) => user.role !== 'executive');

const CorporateProjectReviewStage = ({
  aiDraft,
  busy,
  editable,
  loadToGl,
  project,
  projectValidationIssues,
  reviewCommentary,
  setActiveStage,
  transitionProject,
  updateReviewCommentary
}) => {
  const [pendingAction, setPendingAction] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [handoffOwner, setHandoffOwner] = useState(project.accountable_owner || ownerOptions[0]?.name || '');
  const readOnlyState = ['cancelled', 'archived'].includes(project.status);

  const availableActions = useMemo(
    () => ACTIONS_BY_STATUS[project.status] || [],
    [project.status]
  );

  const openLifecycleAction = (action) => {
    setPendingAction(action);
    setActionComment('');
    setHandoffOwner(project.accountable_owner || ownerOptions[0]?.name || '');
  };

  const submitLifecycleAction = () => {
    if (!pendingAction || !actionComment.trim()) return;
    transitionProject(pendingAction, {
      comment: actionComment.trim(),
      toOwner: pendingAction === 'owner_handoff' ? handoffOwner : undefined
    });
    setPendingAction(null);
  };

  const actionBlocked = (action) => {
    if (busy !== null) return true;
    if (['submit', 'approve'].includes(action) && projectValidationIssues.length > 0) return true;
    return false;
  };

  return (
    <section className="forecast-panel workspace-stage-panel" id="project-stage-review" tabIndex="-1">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">PRJ_04 ● Review &amp; lifecycle</span>
        <h2>FP&amp;A reviews the AI first pass, edits the template, then advances the project</h2>
      </div>
      {readOnlyState && (
        <div className="forecast-submit-note" role="status">
          This project is {project.status}. Financial inputs and source evidence are read-only.
        </div>
      )}
      {aiDraft?.review && (
        <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
          {(aiDraft.review.checks || []).map((check) => (
            <span key={check.name} className={`forecast-health-chip ${check.passed ? 'green' : 'red'}`} title={check.note}>
              {check.passed ? '✓' : '✗'} {check.name}
            </span>
          ))}
        </div>
      )}
      <label className="forecast-submit-narrative">
        <span>Review commentary</span>
        <textarea
          name="project-review-commentary"
          value={reviewCommentary}
          disabled={!editable}
          onChange={(event) => updateReviewCommentary(event.target.value)}
          placeholder="Summarize the project assumptions, material economics, and remaining risks…"
          rows={5}
        />
      </label>
      <ForecastValidationList
        issues={projectValidationIssues}
        onNavigate={(issue) => setActiveStage(issue.stage)}
      />
      <div className="forecast-submit-actions">
        {project.status === 'loaded' && (
          <span className="forecast-health-chip green">
            Loaded{project.loadResult ? ` — ${project.loadResult.insertedRows} rows as ${project.loadResult.scenarioVersion}` : ''}
          </span>
        )}
        {availableActions.map((action) => {
          if (action === 'load') {
            return (
              <button
                key={action}
                type="button"
                className="forecast-primary-button"
                disabled={busy === 'load'}
                onClick={loadToGl}
              >
                <UploadCloud size={15} aria-hidden="true" />
                {busy === 'load' ? 'Writing…' : 'Load to GL_Fact under project dimension'}
              </button>
            );
          }
          return (
            <button
              key={action}
              type="button"
              className={['submit', 'approve', 'reopen'].includes(action) ? 'forecast-primary-button' : 'forecast-secondary-button'}
              disabled={actionBlocked(action)}
              onClick={() => openLifecycleAction(action)}
            >
              {ACTION_COPY[action].label}
            </button>
          );
        })}
      </div>
      {pendingAction && (
        <div className="forecast-lifecycle-dialog" role="dialog" aria-modal="true" aria-labelledby="project-lifecycle-title">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">Lifecycle action</span>
            <h3 id="project-lifecycle-title">{ACTION_COPY[pendingAction].title}</h3>
          </div>
          {pendingAction === 'owner_handoff' && (
            <label className="forecast-submit-narrative">
              <span>New accountable owner</span>
              <select
                name="project-owner-handoff"
                value={handoffOwner}
                onChange={(event) => setHandoffOwner(event.target.value)}
              >
                {ownerOptions.map((user) => (
                  <option key={user.name} value={user.name}>{user.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="forecast-submit-narrative">
            <span>Comment <small>(required)</small></span>
            <textarea
              name="project-lifecycle-comment"
              value={actionComment}
              onChange={(event) => setActionComment(event.target.value)}
              placeholder={ACTION_COPY[pendingAction].prompt}
              rows={3}
            />
          </label>
          <div className="forecast-submit-actions">
            <button
              type="button"
              className="forecast-primary-button"
              disabled={busy !== null || !actionComment.trim() || (pendingAction === 'owner_handoff' && !handoffOwner)}
              onClick={submitLifecycleAction}
            >
              Confirm {ACTION_COPY[pendingAction].label.toLowerCase()}
            </button>
            <button
              type="button"
              className="forecast-secondary-button"
              disabled={busy !== null}
              onClick={() => setPendingAction(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <details className="workspace-change-drawer">
        <summary>GL load lineage and technical detail</summary>
        <div className="workspace-change-content">
          <p className="forecast-submit-note">
            The load writes monthly marginal revenue, operating expense, depreciation (equipment vs
            occupancy by bucket), and capitalized net book value to GL_Fact under {project.project_dim_id}
            {' '}and {project.entity_id}. Loaded project reopens reverse the exact audited batch and
            reserve the next _R version for the corrected reload.
          </p>
        </div>
      </details>
      <ForecastChangeDrawer entries={project.events || []} />
    </section>
  );
};

export default CorporateProjectReviewStage;
